import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { CheckmarkIcon } from '@treams/treams-icons';
import { useHistory } from '@treams/treams-routes';
import {
	Avatar,
	Badge,
	Heading,
	Paragraph,
	Pill,
	PlainCard,
	Popover,
	Skeleton,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import useAuthenticatedUser from '../packages/kakapo-ui-application/hooks/useAuthenticatedUser';

import SpontaneousFeedbackModal from './SpontaneousFeedbackModal';

export default Skeleton(
	function IncomingFeedbackCard(props: { request: graphql.FeedbackRequest; userID: string }) {
		const { t } = useTranslation();
		const viewer = useAuthenticatedUser();
		const history = useHistory();

		const [warning, setWarning] = useState(false);

		const numberOfRecipients = props.request.responses.edges.length;
		const hasRecipientsOverflow = numberOfRecipients > 4;

		const recipients = props.request.responses.edges.slice(0, hasRecipientsOverflow ? 3 : 4);

		const url =
			viewer.id === props.userID
				? `/feedback/${props.request.id}`
				: `/users/${props.userID}/feedback/${props.request.id}`;

		return (
			<>
				<Popover center backdrop visible={warning} onDismiss={() => setWarning(false)}>
					<SpontaneousFeedbackModal
						request={props.request}
						onDismiss={() => setWarning(false)}
						onContinue={() => history.push(url)}
					/>
				</Popover>
				<PlainCard
					onClick={
						props.request.mode === graphql.FeedbackMode.Spontaneous
							? () => setWarning(true)
							: undefined
					}
					to={props.request.mode === graphql.FeedbackMode.Spontaneous ? undefined : url}
				>
					<Flex py={3} alignItems="center">
						<Box flexGrow={1} flexShrink={1}>
							<Paragraph>
								{t('feedback.incoming.card.createdAt', {
									date: moment(props.request.createdAt).format('LL'),
								})}
								{' · '}
								{t('feedback.incoming.card.dueDate', {
									date: moment(props.request.dueDate).format('LL'),
								})}
								{' · '}
								{t('feedback.incoming.card.progress', {
									shared: props.request.responses.edges.filter(
										({ node }) => node.status === graphql.FeedbackResponseStatus.Shared,
									).length,
									total: props.request.responses.edges.length,
								})}
							</Paragraph>
							<Heading mt={2}>{props.request.title || t('feedback.incoming.card.title')}</Heading>
						</Box>
						{props.request.mode === graphql.FeedbackMode.Spontaneous && (
							<Pill mx={2}>{t('feedback.incoming.card.spontaneous')}</Pill>
						)}
						<Flex flexGrow={0} flexShrink={0}>
							{recipients.map(({ node }) => (
								<Box key={node.id} mr={2}>
									<Badge.Container
										badge={
											node.status === graphql.FeedbackResponseStatus.Shared && (
												<Badge color="green" icon={<CheckmarkIcon />} />
											)
										}
									>
										<Avatar size={32} user={node.recipient} />
									</Badge.Container>
								</Box>
							))}

							{hasRecipientsOverflow && (
								<Box mr={2}>
									<Avatar.Overflow size={32} count={numberOfRecipients - 3} />
								</Box>
							)}
						</Flex>
					</Flex>
				</PlainCard>
			</>
		);
	},
	function IncomingFeedbackCardSkeleton() {
		return (
			<PlainCard.Skeleton>
				<Flex alignItems="center">
					<Box py={3} flexGrow={1} flexShrink={1}>
						<Paragraph.Skeleton lines={1} />
						<Heading.Skeleton mt={2} />
					</Box>
					<Flex flexGrow={0} flexShrink={0}>
						<Box mr={2}>
							<Avatar.Skeleton size={32} />
						</Box>
						<Box mr={2}>
							<Avatar.Skeleton size={32} />
						</Box>
						<Box mr={2}>
							<Avatar.Skeleton size={32} />
						</Box>
						<Box mr={2}>
							<Avatar.Skeleton size={32} />
						</Box>
					</Flex>
				</Flex>
			</PlainCard.Skeleton>
		);
	},
);
